// TanStack Query key factories. `workspaceKeys.all` is the one resource
// `tenantPredicate` spares on a workspace switch (see below). `meKeys.detail`
// is also user-scoped in principle — identity doesn't change with the
// current workspace either — but ewt's original design (kept here) lets it
// fall under the coarse eviction anyway: refetching identity on a tenant
// switch is harmless, so it isn't special-cased (see api/me.ts). Every other
// resource's key is rooted at its natural parent scope id (workspaceId for
// surveys/templates, surveyId for a survey's own sub-resources —
// placements/stats/responses) so a single resource can be invalidated
// without disturbing its siblings.
//
// A paginated POST /search endpoint's key follows ewt's own
// `searchKeys.<resource>` idiom instead (see ewt's api/keys.ts): a leading
// "search" tag, then the resource name, then the parent scope id, then the
// request object itself — `["search", "<resource>", parentId, rq]` — kept
// here as each resource's own `.search` method (`surveyKeys.search`,
// `responseKeys.search`) rather than a separate shared object, since every
// other family here is already grouped by resource.
//
// Tenant switch evicts everything except the workspace list via
// `tenantPredicate`: any key whose first element isn't the literal
// "workspaces" tag gets removed, regardless of which parent id it's rooted
// at — the eviction is coarse by design, not keyed per-workspace. A "search"
// tag at position 0 is never "workspaces", so search keys are evicted like
// any other tenant-scoped resource, same as ewt's own `searchKeys.locations`/
// `searchKeys.screens` under this exact predicate.

import type { Query } from "@tanstack/react-query";

export const workspaceKeys = {
    all: ["workspaces"] as const,
};

export const meKeys = {
    detail: ["me"] as const,
};

export const surveyKeys = {
    list: (workspaceId: string) => [workspaceId, "surveys"] as const,
    detail: (surveyId: string) => [surveyId, "survey"] as const,
    search: (workspaceId: string, params: unknown) => ["search", "surveys", workspaceId, params] as const,
    // The invalidation prefix for the `.search` keys above — matches every
    // cached page/params tuple for a workspace regardless of the request
    // body, so a create/patch/delete/steps mutation can drop them all at
    // once instead of guessing which exact `params` a mounted table is using.
    searchRoot: (workspaceId: string) => ["search", "surveys", workspaceId] as const,
    // A mutation key, not a query key: lets any step card see that some
    // steps PUT for the survey is still in flight (useIsMutating).
    putSteps: (surveyId: string) => [surveyId, "survey", "putSteps"] as const,
};

export const templateKeys = {
    list: (workspaceId: string) => [workspaceId, "templates"] as const,
    detail: (templateId: string) => [templateId, "template"] as const,
};

export const securityKeys = {
    detail: (workspaceId: string) => [workspaceId, "security"] as const,
};

export const placementKeys = {
    list: (surveyId: string) => [surveyId, "placements"] as const,
};

export const inviteKeys = {
    summary: (placementId: string) => [placementId, "invites", "summary"] as const,
    emailDocument: (placementId: string) => [placementId, "email"] as const,
    emailBlock: (placementId: string) => [placementId, "email-block"] as const,
};

export const statsKeys = {
    detail: (surveyId: string, params: unknown) => [surveyId, "stats", params] as const,
    // The invalidation prefix: every segment's stats for the survey.
    root: (surveyId: string) => [surveyId, "stats"] as const,
};

export const overviewKeys = {
    detail: (workspaceId: string, params: unknown) => [workspaceId, "overview", params] as const,
    // The invalidation prefix: every window's overview for the workspace.
    root: (workspaceId: string) => [workspaceId, "overview"] as const,
};

export const responseKeys = {
    search: (surveyId: string, params: unknown) => ["search", "responses", surveyId, params] as const,
    // The invalidation prefix for `.search` above (see surveyKeys.searchRoot).
    searchRoot: (surveyId: string) => ["search", "responses", surveyId] as const,
    detail: (responseId: string) => ["responses", responseId] as const,
};

// Rooted at one "audit" tag rather than the workspace: every successful
// mutation drops all of them (api/audit.ts invalidateAuditLog).
export const auditKeys = {
    all: ["audit"] as const,
    search: (workspaceId: string, rq: unknown) => ["audit", "search", workspaceId, rq] as const,
    event: (workspaceId: string, eventId: string) => ["audit", "event", workspaceId, eventId] as const,
};

export function tenantPredicate(query: Pick<Query, "queryKey">): boolean {
    return query.queryKey[0] !== "workspaces";
}
